import { IRideStep, Ride, useUnits } from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { Link } from 'gatsby';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';

function PointsOfInterest({
  isOwner,
  ride,
  onStepPhotosDialogOpen,
}: {
  isOwner: boolean;
  onStepPhotosDialogOpen?: (step: IRideStep) => void;
  ride: Ride | null | undefined;
}): JSX.Element {
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();

  if (ride && ride.steps.length === 0 && !isOwner) return <></>;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box alignItems="center" display="flex" gap={2}>
        <Typography component="h2" fontWeight={700} variant="h6">
          {t('geovelo.rides.detail.points_of_interest')}
        </Typography>
      </Box>
      {(!ride || ride.steps.length > 0) && (
        <Box display="flex" flexDirection="column">
          {ride
            ? ride.steps.map((step, index) => (
                <PointOfInterest
                  isLast={index === ride.steps.length - 1}
                  key={index}
                  onStepPhotosDialogOpen={onStepPhotosDialogOpen}
                  order={index + 1}
                  step={step}
                />
              ))
            : [1, 2, 3].map((key) => <PointOfInterest isLast={key === 3} key={key} order={key} />)}
        </Box>
      )}
      {isOwner && ride && (
        <Box alignSelf="flex-start">
          <Button
            color="primary"
            component={Link}
            size="small"
            startIcon={<Add />}
            to={`/${currentLanguage}/ride-update/${ride.id}?tab=steps`}
            variant="outlined"
          >
            {t('geovelo.rides.detail.completion_card.actions.add_more_steps')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function PointOfInterest({
  order,
  isLast,
  step,
  onStepPhotosDialogOpen,
}: {
  isLast: boolean;
  onStepPhotosDialogOpen?: (step: IRideStep) => void;
  order: number;
  step?: IRideStep;
}): JSX.Element {
  const { toDistance } = useUnits();

  return (
    <Box display="flex">
      <Box display="flex" flexDirection="column" flexShrink={0} gap={2} width={80}>
        <Typography variant="caption">
          {step ? toDistance(step.distanceFromStart || 0) : <Skeleton variant="text" width={50} />}
        </Typography>
        {!isLast && (
          <Box borderLeft="1px dashed #c0c7cc" flexGrow={1} marginBottom={2} marginLeft={2} />
        )}
      </Box>
      <Box display="flex" flexDirection="column" flexGrow={1} gap={2} paddingBottom={2}>
        <Box alignItems="center" display="flex" gap={2}>
          <Box
            alignItems="center"
            borderRadius={16}
            display="flex"
            height={32}
            justifyContent="center"
            sx={{ backgroundColor: '#8eeac2' }}
            width={32}
          >
            <Typography fontWeight={700}>{order}</Typography>
          </Box>
          <Typography fontSize="1.1rem" fontWeight={600} variant="subtitle1">
            {step ? step.title : <Skeleton variant="text" width={200} />}
          </Typography>
        </Box>
        {(!step || step.photos[0] || step.description) && (
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
            {step ? (
              <>
                {step.photos[0] && (
                  <Box
                    borderRadius={4}
                    component={ButtonBase}
                    flexShrink={0}
                    height={210}
                    onClick={() => step && onStepPhotosDialogOpen?.(step)}
                    sx={{
                      backgroundImage: `url(${step.photos[0].thumbnailUrl})`,
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover',
                    }}
                    width={280}
                  />
                )}
                {step.description && (
                  <Box>
                    <Typography>{step.description}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Skeleton
                  height={210}
                  sx={{ borderRadius: 2, flexShrink: 0 }}
                  variant="rectangular"
                  width={280}
                />
                <Box>
                  <Typography variant="body2">
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="90%" />
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PointsOfInterest;
